/**
 * Process-global state slots (request `4f1facd4`, the shared-jwks-resolver trap).
 *
 * `@nodii/grpc-auth` ships SEPARATE subpath bundles (`.`, `/server`, `/verify`,
 * `/client`, …) built by tsup with `splitting: false`, so each bundle INLINES
 * its own copy of a module like `verify/s2s-verify` or `configure`. A plain
 * module-level `let` in one of those modules therefore becomes a DIFFERENT
 * variable per subpath: a service that wires the resolver via the root
 * `setJwksResolver` and then verifies via `@nodii/grpc-auth/server` (whose
 * `verifyS2SToken` reads the /server bundle's copy) sees "JWKS resolver not
 * configured" on every token even though it WAS set — a silent, latent trap.
 * (Caught on nodii-ticketing-service 2026-06-30: 17 files imported
 * `withAuthUnary` from `/server` while the verifier was wired via the root
 * `setJwksResolver`; the /server global was never seen.) The same split hits
 * the `configureGrpcAuth` config (read via `/server`'s check-user-permission).
 *
 * The fix: keep the shared state on `globalThis` under a `Symbol.for` key, so
 * every duplicated module copy reads/writes the SAME slot — the import path can
 * no longer silently split it. (Python module singletons and Go package vars are
 * inherently single-instance, so only the TS subpath build is affected; this
 * file has no Python/Go analogue.)
 *
 * Scope note: `globalThis` + `Symbol.for` are per-REALM, so the slot is shared
 * across every subpath bundle in ONE realm (the normal case) but NOT across
 * `worker_threads` — each worker is a separate realm. This is the correct
 * boundary: the resolver and config hold live FUNCTIONS / objects that cannot
 * be structured-cloned across a worker boundary anyway, so a worker that
 * performs S2S verification must wire its own resolver/config (call
 * `setJwksResolver` / `configureGrpcAuth` inside the worker). The bug this fixes
 * — subpath-bundle duplication WITHIN a realm — is fully resolved.
 */

const NAMESPACE = "@nodii/grpc-auth";

export type GlobalSlot<T> = {
  /** Current value, or `null` when unset. */
  get(): T | null;
  /** Set (or clear with `null`) the process-global value. */
  set(value: T | null): void;
};

/**
 * Build a `globalThis`-backed slot. `name` is namespaced under
 * `@nodii/grpc-auth:` and resolved through `Symbol.for`, so all copies of this
 * module (one per subpath bundle) share the exact same registry entry.
 */
export function processGlobalSlot<T>(name: string): GlobalSlot<T> {
  const key = Symbol.for(`${NAMESPACE}:${name}`);
  const store = globalThis as unknown as Record<symbol, T | null | undefined>;
  return {
    get: () => store[key] ?? null,
    set: (value: T | null) => {
      store[key] = value;
    },
  };
}
